'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils';

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
  labels?: {
    noItems?: string;
    showing?: string; // We'll handle interpolation manually or via a prop
    rowsPerPage?: string;
    previousPage?: string;
    nextPage?: string;
  };
  // Better yet, allow passing pre-formatted strings for showing/noItems
  showingLabel?: string; 
  rowsPerPageLabel?: string;
  noItemsLabel?: string;
}

export function Pagination({ 
  page, 
  pageSize, 
  total, 
  onChange,
  showingLabel,
  rowsPerPageLabel = 'Rows per page',
  noItemsLabel = 'No items found',
}: PaginationProps) {
  const totalPages = Math.ceil(total / pageSize);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onChange(newPage, pageSize);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    onChange(1, newPageSize);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push('...');
      if (totalPages > 1) pages.push(totalPages);
    }
    return pages;
  };

  if (total === 0) {
    return (
      <div className="flex items-center justify-between py-4 text-sm text-muted-foreground">
        {noItemsLabel}
      </div>
    );
  }

  const defaultShowingLabel = `Showing ${startItem} to ${endItem} of ${total}`;

  return (
    <div className="flex flex-col gap-3 border-t border-border bg-muted/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6 sm:py-4">
      <div className="text-sm text-muted-foreground order-2 sm:order-1">
        {showingLabel || defaultShowingLabel}
      </div>

      <div className="order-1 flex items-center justify-center gap-1 overflow-x-auto sm:order-2">
        <button
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          className="p-2 rounded-lg hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-muted-foreground hover:text-foreground"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {getPageNumbers().map((pageNum, index) => {
          if (pageNum === '...') {
            return (
              <span key={`ellipsis-${index}`} className="px-3 py-2 text-muted-foreground">
                ...
              </span>
            );
          }

          const isActive = pageNum === page;

          return (
            <button
              key={pageNum}
              onClick={() => handlePageChange(pageNum as number)}
              className={cn(
                "min-w-[40px] px-3 py-2 rounded-lg font-medium transition-colors",
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              )}
            >
              {pageNum}
            </button>
          );
        })}

        <button
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
          className="p-2 rounded-lg hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-muted-foreground hover:text-foreground"
          aria-label="Next page"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="flex items-center justify-between gap-2 sm:order-3 sm:justify-end">
        <label className="text-sm text-muted-foreground">{rowsPerPageLabel}</label>
        <select
          value={pageSize}
          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
          className="rounded-lg border border-border bg-background px-3 py-1.5 pr-10 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>
      </div>
    </div>
  );
}
